// Libs
import { TFunction } from 'i18next';
import * as yup from 'yup';
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
import { ReasonType } from '~/utils/enum/common';
import { IFormChecklist, IFormInventory, IItemAddOn, IItemVariant } from '~/utils/interface/inventory';

export const schema = (t: TFunction, inventoryId?: string): yup.ObjectSchema<IFormInventory> => {
  return yup.object().shape({
    name: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    productId: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    productDesignation: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    quantity: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    rentalPrice: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    inventoryImage: yup.array().optional(),
    inventoryFile: yup.array().optional(),
    url: yup.string().trim().optional(),
    timeCapacity: yup.string().trim().optional(),
    warehouseLocation: yup.string().trim().optional(),
    brand: yup.string().trim().optional(),
    purchasePrice: yup.string().trim().optional(),
    replacementCosts: yup.string().trim().optional(),
    weight: yup.string().trim().optional(),
    dimensions: yup.string().trim().optional(),
    reason: yup.string().trim().optional(),
    purchaseCost: yup.string().trim().optional(),
    purchaseDate: yup
      .string()
      .trim()
      .when('reason', {
        is: ReasonType.PURCHASED_NEW_PRODUCT,
        then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
        otherwise: (schema) => schema.optional(),
      }),
    note: yup.string().trim().optional(),
    categoryId: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    categoryName: yup.string().trim().optional(),
    description: yup.string().trim().optional(),
    timeLoading: yup.string().optional(),
    timeUnloading: yup.string().optional(),
    timeDelivery: yup.string().optional(),
    timePickup: yup.string().optional(),
    isDamageWaiver: yup.boolean().optional(),
    isDiscountable: yup.boolean().optional(),
    isTaxable: yup.boolean().optional(),
    isShowOnPrint: yup.boolean().optional(),
    isAddOn: yup.boolean().optional(),
    addOns: yup
      .array()
      .of(
        yup.object({
          id: yup.string().trim().optional(),
          name: yup.string().trim().optional(),
          quantity: yup.number().optional(),
          price: yup.string().trim().optional(),
          isRequired: yup.boolean().optional(),
          isShowOnWeb: yup.boolean().optional(),
          productId: yup.string().trim().optional(),
          variantIds: yup.array().of(yup.string().trim().defined()).optional(),
          addOnId: yup
            .string()
            .trim()
            .required(t('common_validate_field_required', { ns: 'common/base' }))
            .test('duplicate-addon', t('common_validate_duplicate', { ns: 'common/base' }), function (value) {
              const rootArray = this.from?.[1]?.value?.addOns ?? [];

              const duplicateCount = rootArray.filter(
                (item: IItemAddOn) => item?.addOnId?.toLowerCase() === value?.toLowerCase()
              ).length;

              return duplicateCount <= 1;
            })
            .test('self-selection', t('common_validate_cannot_choose_itself', { ns: 'common/base' }), function (value) {
              const currentAddOnId = this.parent?.productValidate;

              if (!inventoryId || !currentAddOnId) return true;
              return Number(currentAddOnId) !== Number(inventoryId);
            }),
        })
      )
      .optional(),
    variants: yup.array().optional(),
    checklistItems: yup.array().optional(),
    deleteAddOns: yup.array().optional(),
    deleteFiles: yup.array().optional(),
    deleteVariants: yup.array().optional(),
    deleteChecklistItems: yup.array().optional(),
  });
};

export const schemaVariant = (t: TFunction): yup.ObjectSchema<IItemVariant> => {
  return yup.object().shape({
    id: yup.string().trim().optional(),
    name: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    rentalPrice: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    quantity: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    style: yup.string().trim().optional(),
    length: yup.string().trim().optional(),
    size: yup.string().trim().optional(),
    color: yup.string().trim().optional(),
    height: yup.string().trim().optional(),
    weight: yup.string().trim().optional(),
    description: yup.string().trim().optional(),
    uuid: yup.string().trim().optional(),
    fileId: yup.string().trim().optional(),
    variantImage: yup.object().optional(),
  });
};

export const schemaCheckList = (t: TFunction): yup.ObjectSchema<IFormChecklist> => {
  return yup.object().shape({
    name: yup.string().trim().optional(),
    description: yup.string().trim().optional(),
  });
};

export const defaultValuesInventoryForm: IFormInventory = {
  name: EMPTY_STRING,
  url: EMPTY_STRING,
  productId: EMPTY_STRING,
  brand: EMPTY_STRING,
  productDesignation: EMPTY_STRING,
  quantity: EMPTY_STRING,
  rentalPrice: EMPTY_STRING,
  purchasePrice: EMPTY_STRING,
  replacementCosts: EMPTY_STRING,
  weight: EMPTY_STRING,
  dimensions: EMPTY_STRING,
  warehouseLocation: EMPTY_STRING,
  timeCapacity: EMPTY_STRING,
  note: EMPTY_STRING,
  categoryId: EMPTY_STRING,
  categoryName: EMPTY_STRING,
  description: EMPTY_STRING,
  reason: EMPTY_STRING,
  purchaseCost: EMPTY_STRING,
  purchaseDate: EMPTY_STRING,
  timeDelivery: EMPTY_STRING,
  timeLoading: EMPTY_STRING,
  timeUnloading: EMPTY_STRING,
  timePickup: EMPTY_STRING,
  isDamageWaiver: false,
  isDiscountable: false,
  isTaxable: false,
  isShowOnPrint: false,
  inventoryImage: [],
  inventoryFile: [],
  isAddOn: false,
  addOns: [],
  variants: [],
  deleteAddOns: [],
  deleteFiles: [],
  deleteVariants: [],
  deleteChecklistItems: [],
};

export const defaultValuesVariantForm: IItemVariant = {
  id: EMPTY_STRING,
  name: EMPTY_STRING,
  length: EMPTY_STRING,
  size: EMPTY_STRING,
  height: EMPTY_STRING,
  color: EMPTY_STRING,
  weight: EMPTY_STRING,
  style: EMPTY_STRING,
  description: EMPTY_STRING,
  quantity: EMPTY_STRING,
  rentalPrice: EMPTY_STRING,
  variantImage: {},
  uuid: EMPTY_STRING,
  fileId: EMPTY_STRING,
};
